import {computed, inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ServiceRelations} from '@features/service/domain/models/service-relations.model';
import {Location} from '@features/service/domain/models/location.model';
import {Drawer} from '@features/service/domain/models/drawer.model';
import {Worker} from '@features/service/domain/models/worker.model';
import {SERVICE_REPOSITORY} from '@features/service/domain/tokens/service.token';
import {SERVICE_STORE} from '@features/service/domain/tokens/service-store.token';
import {ServiceStatePort} from '@features/service/domain/port/service-state.port';
import {ChangeDrawerUseCase} from '../use-cases/change-drawer.use-case';
import {SERVICE_STORE_STORAGE} from "@features/service/infrastructure/tokens/service-store-storage.token";

@Injectable({providedIn: 'root'})
export class ServiceStateService implements ServiceStatePort {
  private readonly serviceStore = inject(SERVICE_STORE);
  private readonly storage = inject(SERVICE_STORE_STORAGE);
  private readonly serviceRepository = inject(SERVICE_REPOSITORY);
  private readonly changeDrawerUseCase = inject(ChangeDrawerUseCase);

  setServiceRelations(relations: ServiceRelations): void {
    this.serviceStore.setServiceRelations(relations);
  }

  setIsWorkersOwnedDevice(isWorkersOwnedDevice: boolean): void {
    this.serviceStore.setIsWorkersOwnedDevice(isWorkersOwnedDevice);
  }

  clear(): Observable<void> {
    this.serviceStore.reset();

    return this.storage.clear();
  }

  clearWorker(): void {
    const state = this.serviceStore.state();
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;

    if (isWorkersOwnedDevice) {
      this.serviceStore.setSelection({
        location: null,
        drawer: null,
        worker: null,
      });
    } else {
      this.serviceStore.setSelection({
        ...state.selection,
        worker: null,
      });
    }
  }

  clearLocation(): void {
    const state = this.serviceStore.state();
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;

    if (isWorkersOwnedDevice) {
      this.serviceStore.setSelection({...state.selection, location: null, drawer: null});
    } else {
      this.serviceStore.setSelection({...state.selection, location: null, drawer: null, worker: null});
    }
  }

  clearDrawer(): void {
    const state = this.serviceStore.state();
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;

    if (isWorkersOwnedDevice) {
      this.serviceStore.setSelection({...state.selection, drawer: null});
    } else {
      this.serviceStore.setSelection({...state.selection, drawer: null, worker: null});
    }
  }

  clearSelection(): void {
    this.serviceStore.setSelection({
      location: null,
      drawer: null,
      worker: null,
    });
  }

  readonly shouldShowLocations = computed<boolean>(() => {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;

    if (isWorkersOwnedDevice) {
      return !!(selection.worker && !selection.drawer);
    } else {
      return !selection.drawer;
    }
  });

  readonly shouldShowDrawers = computed<boolean>(() => {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;

    if (isWorkersOwnedDevice) {
      return !!(selection.worker && selection.location && !selection.drawer);
    } else {
      return !!(selection.location && !selection.drawer);
    }
  });

  readonly shouldShowWorkers = computed<boolean>(() => {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;

    if (isWorkersOwnedDevice) {
      return !selection.worker && !selection.location && !selection.drawer;
    } else {
      return !!(selection.location && selection.drawer);
    }
  });

  readonly filteredLocations = computed<Location[]>(() => {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;
    const allLocations = state.locations;
    const relations = state.serviceRelations;

    if (!relations) {
      return [];
    }

    if (!isWorkersOwnedDevice) {
      const locationIdsWithDrawers = Object.keys(relations.locationsToCashDrawers);

      return allLocations.filter((location) => locationIdsWithDrawers.includes(location.id));
    }

    if (!selection.worker) {
      return [];
    }

    const locationIds = relations.workersToLocations[selection.worker.id] || [];

    return allLocations.filter((location) => locationIds.includes(location.id));
  });

  readonly filteredDrawers = computed<Drawer[]>(() => {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};
    const allDrawers = state.drawers;
    const relations = state.serviceRelations;

    if (!selection.location) {
      return [];
    }


    if (!relations) {
      return [];
    }

    const drawerIds = relations.locationsToCashDrawers[selection.location.id] || [];

    return allDrawers.filter((drawer) => drawerIds.includes(drawer.id));
  });

  removeCurrentLocation(): void {
    this.serviceStore.setSelection({...this.serviceStore.state().selection, location: null});
  }

  insertLocations(locations: Location[]): void {
    const currentLocations = this.serviceStore.state().locations;
    const newLocations = [...currentLocations, ...locations];

    this.serviceStore.setLocations(newLocations);
  }

  removeLocations(locations: Location[]): void {
    const currentLocations = this.serviceStore.state().locations;
    const locationIdsToRemove = new Set(locations.map(l => l.id));
    const filteredLocations = currentLocations.filter(
      location => !locationIdsToRemove.has(location.id)
    );

    this.serviceStore.setLocations(filteredLocations);
  }

  getCurrentLocation(): Location | null {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};

    return selection.location;
  }

  setCurrentLocation(location: Location): void {
    this.serviceStore.setSelection({...this.serviceStore.state().selection, location});
  }

  getLocationsFromServer(): Observable<Location[]> {
    return this.serviceRepository.getLocations();
  }

  getAllLocations(): Location[] {
    return this.serviceStore.state().locations;
  }

  removeCurrentDrawer(): void {
    this.serviceStore.setSelection({...this.serviceStore.state().selection, drawer: null});
  }

  getCurrentDrawer(): Drawer | null {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};

    return selection.drawer;
  }

  setCurrentDrawer(drawer: Drawer): void {
    this.serviceStore.setSelection({...this.serviceStore.state().selection, drawer});
  }

  getDrawersFromServer(): Observable<Drawer[]> {
    return this.serviceRepository.getDrawers();
  }

  getAllDrawers(): Drawer[] {
    return this.serviceStore.state().drawers;
  }

  removeCurrentWorker(): void {
    this.serviceStore.setSelection({...this.serviceStore.state().selection, worker: null});
  }

  getCurrentWorker(): Worker | null {
    const state = this.serviceStore.state();
    const selection = state.selection ?? {location: null, drawer: null, worker: null};

    return selection.worker;
  }

  getWorkersFromServer(): Observable<Worker[]> {
    return this.serviceRepository.getWorkers();
  }

  getAllWorkers(): Worker[] {
    return this.serviceStore.state().workers;
  }

  setCurrentWorker(worker: Worker): void {
    this.serviceStore.setSelection({...this.serviceStore.state().selection, worker});
  }

  insertDrawers(drawers: Drawer[]): void {
    const currentDrawers = this.serviceStore.state().drawers;
    const newDrawers = [...currentDrawers, ...drawers];

    this.serviceStore.setDrawers(newDrawers);
  }

  insertWorkers(workers: Worker[]): void {
    const currentWorkers = this.serviceStore.state().workers;
    const newWorkers = [...currentWorkers, ...workers];

    this.serviceStore.setWorkers(newWorkers);
  }

  changeDrawer(): void {
    this.changeDrawerUseCase.execute();
  }
}
